import { useLocation, useParams } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { LocalizedLink } from "@/components/LocalizedLink";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  customItems?: BreadcrumbItem[];
  className?: string;
}

// Multi-language route labels
const routeLabelsMap: Record<string, Record<string, string>> = {
  tr: {
    home: "Ana Sayfa",
    treatments: "Tedaviler",
    blog: "Blog",
    gallery: "Galeri",
    compare: "Tedavi Karşılaştırma",
    "hair-analysis": "Saç Analizi",
    "book-consultation": "Randevu Al",
    admin: "Admin Paneli",
    "admin-gallery": "Galeri Yönetimi",
    "patient-dashboard": "Hasta Paneli",
    auth: "Giriş Yap",
    page: "Sayfa",
    search: "Arama",
    "privacy-policy": "Gizlilik Politikası",
    "terms-of-service": "Hizmet Şartları",
    "gdpr-policy": "KVKK/GDPR Politikası",
    "data-deletion": "Veri Silme",
    "thank-you": "Teşekkürler",
  },
  en: {
    home: "Home",
    treatments: "Treatments",
    blog: "Blog",
    gallery: "Gallery",
    compare: "Treatment Comparison",
    "hair-analysis": "Hair Analysis",
    "book-consultation": "Book Consultation",
    admin: "Admin Panel",
    "admin-gallery": "Gallery Management",
    "patient-dashboard": "Patient Dashboard",
    auth: "Login",
    page: "Page",
    search: "Search",
    "privacy-policy": "Privacy Policy",
    "terms-of-service": "Terms of Service",
    "gdpr-policy": "GDPR Policy",
    "data-deletion": "Data Deletion",
    "thank-you": "Thank You",
  },
  fr: {
    home: "Accueil",
    treatments: "Traitements",
    blog: "Blog",
    gallery: "Galerie",
    compare: "Comparaison des Traitements",
    "hair-analysis": "Analyse Capillaire",
    "book-consultation": "Réserver",
    admin: "Panneau Admin",
    "admin-gallery": "Gestion Galerie",
    "patient-dashboard": "Tableau de Bord Patient",
    auth: "Connexion",
    page: "Page",
    search: "Recherche",
    "privacy-policy": "Politique de Confidentialité",
    "terms-of-service": "Conditions de Service",
    "gdpr-policy": "Politique RGPD",
    "data-deletion": "Suppression des Données",
    "thank-you": "Merci",
  },
  nl: {
    home: "Home",
    treatments: "Behandelingen",
    blog: "Blog",
    gallery: "Galerij",
    compare: "Behandeling Vergelijking",
    "hair-analysis": "Haaranalyse",
    "book-consultation": "Afspraak Maken",
    admin: "Admin Paneel",
    "admin-gallery": "Galerij Beheer",
    "patient-dashboard": "Patiënt Dashboard",
    auth: "Inloggen",
    page: "Pagina",
    search: "Zoeken",
    "privacy-policy": "Privacybeleid",
    "terms-of-service": "Servicevoorwaarden",
    "gdpr-policy": "AVG Beleid",
    "data-deletion": "Gegevens Verwijderen",
    "thank-you": "Bedankt",
  },
  ar: {
    home: "الرئيسية",
    treatments: "العلاجات",
    blog: "المدونة",
    gallery: "المعرض",
    compare: "مقارنة العلاجات",
    "hair-analysis": "تحليل الشعر",
    "book-consultation": "حجز موعد",
    admin: "لوحة الإدارة",
    "admin-gallery": "إدارة المعرض",
    "patient-dashboard": "لوحة المريض",
    auth: "تسجيل الدخول",
    page: "صفحة",
    search: "بحث",
    "privacy-policy": "سياسة الخصوصية",
    "terms-of-service": "شروط الخدمة",
    "gdpr-policy": "سياسة GDPR",
    "data-deletion": "حذف البيانات",
    "thank-you": "شكراً",
  },
};

export const Breadcrumb = ({ customItems, className }: BreadcrumbProps) => {
  const location = useLocation();
  const { language } = useLanguage();
  const labels = routeLabelsMap[language] || routeLabelsMap.en;

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) return customItems;

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: labels.home, href: "/" }
    ];

    let currentPath = "";

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Get localized label or format segment nicely
      const label = labels[segment] || segment.split("-").map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(" ");

      // If it's the last segment, don't add href (current page)
      if (index === pathSegments.length - 1) {
        breadcrumbs.push({ label });
      } else {
        breadcrumbs.push({ label, href: currentPath });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": item.href ? `https://hairartclinics.com${item.href}` : undefined
    }))
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <nav
        aria-label="Breadcrumb"
        className={cn("py-4 px-4 md:px-6 bg-muted/30", className)}
      >
        <ol className="flex items-center space-x-2 text-sm max-w-7xl mx-auto">
          {breadcrumbs.map((item, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />
              )}
              {item.href ? (
                <LocalizedLink
                  to={item.href}
                  className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  {index === 0 && <Home className="h-4 w-4 mr-1" />}
                  <span>{item.label}</span>
                </LocalizedLink>
              ) : (
                <span className="flex items-center text-foreground font-medium">
                  {index === 0 && <Home className="h-4 w-4 mr-1" />}
                  <span>{item.label}</span>
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
};
